import {
  BridgeMessage,
  ClipboardData,
  IntercomAction,
  MessagePayload,
  MessageType,
  PaymentDetails,
  PaymentResult,
  ShareContent,
  ShareResult,
  BaseUserBiometricsData,
  UserBiometricsData,
  MobileLoginData,
  LoginCredentials,
} from "@shared/services/webview/types";
import { UserProperties } from "@shared/types";
import { IS_WEB_VIEW } from "common/constants.ts";
import * as Sentry from "@sentry/react";
import { MS_IN_MINUTE } from "@shared/common/constants.ts";

export const postMessageToNative = <T = MessagePayload>(
  message: Partial<BridgeMessage<T>>
): void => {
  if (!IS_WEB_VIEW) return;
  if (window.postMessageToNative) {
    const fullMessage = {
      id: window.generateId(),
      ...message,
    } as BridgeMessage<T>;
    window.postMessageToNative(fullMessage);
  } else {
    Sentry.captureException(
      "WebView (React): window.postMessageToNative is not available."
    );
  }
};

export const sendMessageToNativeAndWaitForResponse = <
  RequestPayload = MessagePayload,
  ResponsePayload = MessagePayload
>(
  type: MessageType | string,
  payload?: RequestPayload,
  timeoutMs?: number
): Promise<ResponsePayload> => {
  if (IS_WEB_VIEW && window.sendMessageToNativeAndWaitForResponse) {
    return window.sendMessageToNativeAndWaitForResponse<
      RequestPayload,
      ResponsePayload
    >(type, payload, timeoutMs);
  } else {
    Sentry.captureException(
      `WebView (React): window.sendMessageToNativeAndWaitForResponse is not available for ${type}.`
    );
    return Promise.reject(new Error("Bridge to native not available."));
  }
};

export const requestBiometricLogin = async (): Promise<MobileLoginData> => {
  return sendMessageToNativeAndWaitForResponse<undefined, MobileLoginData>(
    MessageType.BIOMETRIC_LOGIN_REQUEST
  );
};

export const toggleUserBiometric = async (): Promise<
  BaseUserBiometricsData | undefined
> => {
  return sendMessageToNativeAndWaitForResponse<
    undefined,
    BaseUserBiometricsData | undefined
  >(MessageType.TOGGLE_USER_BIOMETRIC);
};

export const requestBiometricDisplayData =
  async (): Promise<UserBiometricsData> => {
    return sendMessageToNativeAndWaitForResponse<undefined, UserBiometricsData>(
      MessageType.GET_BIOMETRIC_DATA
    );
  };

export const initiatePayment = async (
  details: PaymentDetails
): Promise<PaymentResult> => {
  return sendMessageToNativeAndWaitForResponse<PaymentDetails, PaymentResult>(
    MessageType.INITIATE_PAYMENT_REQUEST,
    details,
    10 * MS_IN_MINUTE
  );
};

export const clearBadgeCount = async (): Promise<void> => {
  return postMessageToNative({
    type: MessageType.CLEAR_BADGE_COUNT,
  });
};

export const shareContent = async (
  content: ShareContent
): Promise<ShareResult> => {
  return sendMessageToNativeAndWaitForResponse<ShareContent, ShareResult>(
    MessageType.SHARE_REQUEST,
    content
  );
};

export const triggerSaveBiometric = async (
  credentials: LoginCredentials
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.TRIGGER_SAVE_BIOMETRICS,
    payload: credentials,
  });
};

export const triggerClearBiometric = async (email?: string): Promise<void> => {
  return postMessageToNative({
    type: MessageType.CLEAR_BIOMETRICS,
    payload: email,
  });
};

export const userUpdated = async (
  user: UserProperties | undefined
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.USER_UPDATED,
    payload: user,
  });
};

export const requestLeaveReview = async (): Promise<void> => {
  return sendMessageToNativeAndWaitForResponse<undefined, void>(
    MessageType.LEAVE_REVIEW
  );
};

export const requestIntercomAction = async (
  actionData: IntercomAction
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.REQUEST_INTERCOM_ACTION,
    payload: actionData,
  });
};

export const requestClipboardData = async (): Promise<ClipboardData> => {
  return sendMessageToNativeAndWaitForResponse<void, ClipboardData>(
    MessageType.REQUEST_CLIPBOARD_DATA
  );
};

export const copyToClipboard = async (text: string): Promise<void> => {
  return sendMessageToNativeAndWaitForResponse<ClipboardData, void>(
    MessageType.COPY_TO_CLIPBOARD,
    { clipboardText: text }
  );
};

export const themeUpdated = async (isDarkMode: boolean): Promise<void> => {
  return postMessageToNative({
    type: MessageType.THEME_UPDATED,
    payload: { isDarkMode },
  });
};
